/**
 * The sections of Concrete Maturity Inputs (/maturity-inputs), shown as the
 * page's grey sidebar. The selected section is the ?tab= query value.
 *
 * Kept apart from the page so DashboardLayout can build the header
 * breadcrumb (Concrete Maturity Inputs > ASTM C1074 > TTF - Temperature Time
 * Factor (Nurse-Saul))
 * without importing the page.
 */
import type { TFunction } from 'i18next';

export type MaturitySection = 'introduction' | 'astm-c1074' | 'nurse-saul' | 'arrhenius' | 'determine';

export const MATURITY_SECTIONS: MaturitySection[] = ['introduction', 'astm-c1074', 'nurse-saul', 'arrhenius', 'determine'];

/** Links without ?tab= open the Introduction. */
export const DEFAULT_MATURITY_SECTION: MaturitySection = 'introduction';

/** The sections listed (indented) under ASTM C1074 in the side menu. */
export const isAstmSubsection = (section: MaturitySection): boolean =>
  section === 'nurse-saul' || section === 'arrhenius' || section === 'determine';

const isSection = (v: string | null): v is MaturitySection =>
  !!v && (MATURITY_SECTIONS as string[]).includes(v);

/** Guide pages from before the sidebar (?tab=introduction&guide=...). */
const LEGACY_GUIDE: Record<string, MaturitySection> = {
  introduction: 'introduction',
  'astm-c1074': 'astm-c1074',
  'temperature-time-factor': 'nurse-saul',
  'equivalent-age': 'arrhenius',
};

/** The section a query string selects, honouring the old ?guide= links. */
export const sectionFromSearch = (params: URLSearchParams): MaturitySection => {
  const tab = params.get('tab');
  const guide = params.get('guide');
  if (tab === 'introduction' && guide && LEGACY_GUIDE[guide]) return LEGACY_GUIDE[guide];
  return isSection(tab) ? tab : DEFAULT_MATURITY_SECTION;
};

/** Side menu label for each section; the breadcrumb uses the same names. */
export const sectionLabel = (section: MaturitySection, t: TFunction): string => {
  switch (section) {
    case 'introduction':
      return t('maturityGuide.nav.introduction', 'Introduction');
    case 'astm-c1074':
      return t('maturityGuide.nav.astm', 'ASTM C1074');
    case 'nurse-saul':
      return t('maturityGuide.nav.ttfMenu', 'TTF - Temperature Time Factor (Nurse-Saul)');
    case 'arrhenius':
      return t('maturityGuide.nav.eaMenu', 'EA - Equivalent Age (Arrhenius)');
    case 'determine':
      return t('maturityGuide.nav.datumQMenu', 'Datum Temperature & Q-Value');
  }
};

/** Breadcrumb after the page name: the method pages sit under ASTM C1074. */
export const sectionCrumbs = (section: MaturitySection, t: TFunction): string[] => {
  const astm = t('maturityGuide.nav.astm', 'ASTM C1074');
  switch (section) {
    case 'introduction':
      return [t('maturityGuide.nav.introduction', 'Introduction')];
    case 'astm-c1074':
      return [astm];
    case 'nurse-saul':
    case 'arrhenius':
    case 'determine':
      return [astm, sectionLabel(section, t)];
  }
};
